'use client'

import { useState, useEffect } from 'react'
import UserRankBadge from '@/components/UserRankBadge'
import { TrophyIcon } from '@/components/Icons'

interface LeaderboardUser {
  id: string
  username: string
  name: string
  rating: number
  maxRating: number
  problemsSolved: number
  contestsJoined: number
  rank: string
  userRank: string
  country?: string
}

export default function LeaderboardPage() {
  const [users, setUsers] = useState<LeaderboardUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState<'rating' | 'problems'>('rating')

  useEffect(() => {
    fetchLeaderboard()
  }, [filter])

  const fetchLeaderboard = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`/api/leaderboard?sortBy=${filter}`)
      const data = await response.json()
      if (response.ok) {
        setUsers(data)
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getRatingColor = (rating: number) => {
    if (rating >= 2400) return 'text-red-600'
    if (rating >= 2100) return 'text-yellow-600'
    if (rating >= 1900) return 'text-purple-600'
    if (rating >= 1600) return 'text-blue-600'
    if (rating >= 1400) return 'text-green-600'
    return 'text-gray-600'
  }

  const getRankIcon = (position: number) => {
    if (position === 1) return <TrophyIcon className="inline-block w-6 h-6 text-yellow-400" />
    if (position === 2) return <TrophyIcon className="inline-block w-6 h-6 text-gray-400" />
    if (position === 3) return <TrophyIcon className="inline-block w-6 h-6 text-amber-600" />
    return <span className="text-lg font-bold text-gray-600">#{position}</span>
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="bg-dark-600 h-8 w-64 rounded mb-8"></div>
          <div className="space-y-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="bg-dark-600 h-16 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-4">Leaderboard</h1>
        <p className="text-gray-200 mb-6">
          Compete with the best mathematicians worldwide
        </p>

        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setFilter('rating')}
            className={`px-4 py-2 rounded-md font-medium ${
              filter === 'rating'
                ? 'bg-primary-600 text-white'
                : 'bg-dark-700 text-gray-200 hover:bg-dark-600'
            }`}
          >
            By Rating
          </button>
          <button
            onClick={() => setFilter('problems')}
            className={`px-4 py-2 rounded-md font-medium ${
              filter === 'problems'
                ? 'bg-primary-600 text-white'
                : 'bg-dark-700 text-gray-200 hover:bg-dark-600'
            }`}
          >
            By Problems Solved
          </button>
        </div>
      </div>

      {users.length === 0 ? (
        <div className="text-center py-12">
          <span className="text-xl">📈</span>
          <p className="text-gray-200 text-lg">No users on the leaderboard yet</p>
          <p className="text-gray-300">Start solving problems to appear here!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {users.map((user, index) => (
            <div
              key={user.id}
              className={`card p-6 ${
                index < 3 ? 'ring-2 ring-primary-400/30 bg-primary-900/20' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    {getRankIcon(index + 1)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-semibold text-white">
                        {user.name || user.username}
                      </h3>
                      <span className="text-gray-300">@{user.username}</span>
                      {user.userRank && user.userRank !== 'User' && (
                        <UserRankBadge rank={user.userRank} size="sm" showLabel={false} />
                      )}
                      {user.country && (
                        <span className="text-sm text-gray-400">
                          📍 {user.country}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className={`font-bold ${getRatingColor(user.rating)}`}>
                        {user.rating}
                      </span>
                      <span className="text-sm text-gray-300">
                        Max: {user.maxRating}
                      </span>
                      <span className="text-sm text-gray-300">
                        Rank: {user.rank}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-8 text-center">
                  <div>
                    <div className="text-2xl font-bold text-white">
                      {filter === 'rating' ? user.rating : user.problemsSolved}
                    </div>
                    <div className="text-sm text-gray-400">
                      {filter === 'rating' ? 'Rating' : 'Problems'}
                    </div>
                  </div>
                  
                  <div>
                    <div className="text-lg font-semibold text-gray-200">
                      {user.problemsSolved}
                    </div>
                    <div className="text-xs text-gray-400">Solved</div>
                  </div>
                  
                  <div>
                    <div className="text-lg font-semibold text-gray-200">
                      {user.contestsJoined}
                    </div>
                    <div className="text-xs text-gray-400">Contests</div>
                  </div>
                </div>
              </div>

              {index < 3 && (
                <div className="mt-4 pt-4 border-t border-primary-600/30">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-primary-400 font-medium">
                      🎉 Top {index + 1} performer
                    </span>
                    <span className="text-primary-300">
                      Keep up the great work!
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}

          {users.length >= 10 && (
            <div className="text-center mt-8">
              <button className="btn btn-secondary">
                Load More Users
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-12 grid md:grid-cols-3 gap-6">
        <div className="card p-6 text-center">
          <span className="text-xl">🏆</span>
          <h3 className="text-lg font-semibold mb-2 text-white">Rating System</h3>
          <p className="text-gray-300 text-sm">
            Earn rating points by solving problems and participating in contests.
          </p>
        </div>
        
        <div className="card p-6 text-center">
          <span className="text-xl">🏅</span>
          <h3 className="text-lg font-semibold mb-2 text-white">Achievements</h3>
          <p className="text-gray-300 text-sm">
            Unlock achievements and climb through different ranks.
          </p>
        </div>
        
        <div className="card p-6 text-center">
          <span className="text-xl">📈</span>
          <h3 className="text-lg font-semibold mb-2 text-white">Progress Tracking</h3>
          <p className="text-gray-300 text-sm">
            Track your progress and see how you compare with others.
          </p>
        </div>
      </div>
    </div>
  )
}